import React, { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Container, Section, SectionHeading } from '@/components/site/Section';
import { ProductCard, PriceTag } from '@/components/site/ProductCard';
import { CategoryTabs, ProductGridSkeleton } from './LandingPage';
import { useCatalog } from '@/state/catalog';

export default function ProductsPage() {
  const catalog = useCatalog();
  const [sp, setSp] = useSearchParams();
  const cat = sp.get('kategori') ?? 'alle';
  const setCat = (id) => {
    const next = new URLSearchParams(sp);
    if (id === 'alle') next.delete('kategori');
    else next.set('kategori', id);
    setSp(next, { replace: true });
  };
  useEffect(() => {
    document.title = 'Produkter og priser · Hardanger Rock';
  }, []);

  const products = useMemo(() => (cat === 'alle' ? catalog.products : catalog.products.filter((p) => p.category === cat)), [catalog.products, cat]);
  const catLabel = (id) => catalog.categories.find((c) => c.id === id)?.label ?? id;

  return (
    <div data-testid="products-page">
      <Section className="border-b border-[var(--hr-hairline)] bg-[var(--hr-bg)] py-12 sm:py-16">
        <Container>
          <nav aria-label="Brødsmuler" className="mb-4 text-[13px] text-[var(--hr-muted)]">
            <Link to="/" className="hover:text-[var(--hr-heading)]">
              Forsiden
            </Link>
            <span className="mx-2 text-[var(--hr-muted-2)]">/</span>
            <span className="text-[var(--hr-text)]">Produkter og priser</span>
          </nav>
          <SectionHeading title="Produkter og priser" lead="Priser eks. mva pr. tonn, netto pr. 10 dager. Stein og store volum prises per prosjekt.">
            <CategoryTabs categories={catalog.categories} value={cat} onChange={setCat} testPrefix="products-category" />
          </SectionHeading>
        </Container>
      </Section>

      <Section className="py-12 sm:py-16">
        <Container>
          {catalog.status === 'loading' && <ProductGridSkeleton />}
          {catalog.status === 'error' && (
            <div className="hr-surface flex flex-col items-start gap-3 p-6" data-testid="products-error">
              <p className="text-[15px] font-medium text-[var(--hr-heading)]">Kunne ikke hente produktene</p>
              <p className="text-[14px] text-[var(--hr-muted)]">{catalog.error}</p>
              <Button variant="outline" onClick={catalog.reload}>
                Prøv igjen
              </Button>
            </div>
          )}
          {catalog.status === 'ready' && (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="products-grid">
              {products.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          )}
        </Container>
      </Section>

      {catalog.status === 'ready' && (
        <Section tone="muted" className="py-14 sm:py-20">
          <Container>
            <SectionHeading eyebrow="Prisliste" title="Alle fraksjoner samlet" lead="Priser eks. mva pr. tonn. Netto pr. 10 dager. Prosjektpris på forespørsel." />
            <div className="hr-surface mt-8 overflow-hidden">
              <Table data-testid="price-table">
                <TableHeader>
                  <TableRow className="bg-[var(--hr-surface-2)] hover:bg-[var(--hr-surface-2)]">
                    <TableHead className="h-11 text-[12px] font-semibold uppercase tracking-wide text-[var(--hr-muted)]">Produkt</TableHead>
                    <TableHead className="h-11 text-[12px] font-semibold uppercase tracking-wide text-[var(--hr-muted)]">Kategori</TableHead>
                    <TableHead className="hidden h-11 text-[12px] font-semibold uppercase tracking-wide text-[var(--hr-muted)] md:table-cell">Bruk</TableHead>
                    <TableHead className="h-11 text-right text-[12px] font-semibold uppercase tracking-wide text-[var(--hr-muted)]">Pris</TableHead>
                    <TableHead className="h-11 w-[1%]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {catalog.products.map((p) => (
                    <TableRow key={p.slug} className="hover:bg-[var(--hr-surface-2)]" data-testid={`price-row-${p.slug}`}>
                      <TableCell className="py-3.5 font-medium text-[var(--hr-heading)]">
                        <Link to={`/produkter/${p.slug}`} className="hover:underline">
                          {p.name}
                        </Link>
                      </TableCell>
                      <TableCell className="py-3.5 text-[var(--hr-muted)]">{catLabel(p.category)}</TableCell>
                      <TableCell className="hidden py-3.5 text-[var(--hr-muted)] md:table-cell">{p.uses.join(' · ')}</TableCell>
                      <TableCell className="py-3.5 text-right">
                        <PriceTag product={p} />
                      </TableCell>
                      <TableCell className="py-3.5">
                        <Button asChild variant="ghost" size="sm" className="h-8 rounded-[8px] text-[13px]">
                          <Link to={`/produkter/${p.slug}`} aria-label={`Åpne ${p.name}`}>
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Container>
        </Section>
      )}
    </div>
  );
}
